// src/pages/chat/ChatPoliciesAdminPage.tsx
//
// Chat policy admin — governs who may create/configure what across
// conversations (chat-policies CRUD + entity lookup + context resolution).
// Admin-gated (AdminRoute, see App.tsx), same tabbed-inspector shape as
// /admin/context and /admin/authz. See docs/ux/chat-ia.md.

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { ChatPoliciesPanel } from '@/features/chat/ChatPoliciesPanel';
import { ResolveChatContextPanel } from '@/features/chat/ResolveChatContextPanel';

type Tab = 'policies' | 'resolve';
const TABS: Tab[] = ['policies', 'resolve'];

export const ChatPoliciesAdminPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'policies';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t('chat.policies.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('chat.policies.pageSubtitle')}</p>
      </div>

      <div role="tablist" aria-label={t('chat.policies.title')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`chat.policies.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'policies' && <ChatPoliciesPanel />}
      {tab === 'resolve' && <ResolveChatContextPanel />}
    </div>
  );
});

export default ChatPoliciesAdminPage;
